import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";

export default function Footer() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, visibleItems: cardItems } = useStaggerAnimation(2, { threshold: 0.1 });
  const { ref: bottomRef, isVisible: bottomVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <footer className="bg-[#f0fafb]">
      <div className="py-16 border-t border-[#d6eff3]">
        <div className="max-w-[1240px] mx-auto px-6">
          <div
            className="mb-10"
            ref={titleRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: titleVisible ? 1 : 0,
              transform: titleVisible ? "translateY(0)" : "translateY(25px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-1">ACCESS</p>
            <h2 className="text-2xl font-bold text-gray-800">施設情報</h2>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12"
            ref={cardsRef as React.RefObject<HTMLDivElement>}
          >
            {/* ヘルパーステーションみりん */}
            <div
              className="bg-white rounded-2xl overflow-hidden"
              style={{
                opacity: cardItems[0] ? 1 : 0,
                transform: cardItems[0] ? "translateY(0)" : "translateY(35px)",
                transition: "opacity 0.8s ease, transform 0.8s ease",
              }}
            >
              <div className="w-full h-56 bg-white flex items-center justify-center p-4">
                <img
                  src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/17bed99ddde88f43a3a3a9430f8812b6.png"
                  alt="ヘルパーステーションみりん"
                  className="h-44 w-auto object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="text-sm font-bold text-gray-800 mb-3">
                  ヘルパーステーションみりん
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {["訪問介護", "障害福祉サービス"].map((t) => (
                    <span key={t} className="text-xs border border-[#3ab5c6] text-[#3ab5c6] px-2 py-0.5 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  〒448-0001 愛知県刈谷市井ケ谷町下前田6番地3<br />
                  コーポ龍美218号室<br />
                  TEL: <a href="tel:0566-68-6220" className="text-[#3ab5c6] hover:underline cursor-pointer">0566-68-6220</a>　/　FAX: 0566-68-6221<br />
                  <span className="text-gray-400">介護指定番号: 2372902144　/　障害指定番号: 2313300887</span>
                </p>
              </div>
            </div>

            {/* 株式会社FD */}
            <div
              className="bg-white rounded-2xl overflow-hidden"
              style={{
                opacity: cardItems[1] ? 1 : 0,
                transform: cardItems[1] ? "translateY(0)" : "translateY(35px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              <div className="w-full h-56 bg-white flex items-center justify-center p-4">
                <img
                  src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/a553fa43e835a1f64a2cb3b529e84374.png"
                  alt="株式会社FD"
                  className="h-44 w-auto object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="text-sm font-bold text-gray-800 mb-3">
                  株式会社FD（本社）
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {["Future Diver", "本社"].map((t) => (
                    <span key={t} className="text-xs border border-[#3ab5c6] text-[#3ab5c6] px-2 py-0.5 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  〒448-0002 愛知県刈谷市一里山町中本山67番地10<br />
                  TEL: <a href="tel:080-6952-9336" className="text-[#3ab5c6] hover:underline cursor-pointer">080-6952-9336</a><br />
                  <span className="text-gray-400">法人番号: 8180301040637</span>
                </p>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div
            className="border-t border-[#d6eff3] pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
            ref={bottomRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: bottomVisible ? 1 : 0,
              transform: bottomVisible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <div>
              <a href="/" className="block mb-1">
                <img
                  src="https://storage.readdy-site.link/project_files/26e3b86f-cc28-43d6-ae72-d910dd967b3f/1d15724a-ec62-4ee1-9e78-2a32eb541b2d_logo_fd_NoBG.png?v=2eb01d7e8656ed8da8e114c01d14912f"
                  alt="株式会社FD"
                  className="h-10 w-auto object-contain"
                />
              </a>
            </div>

            <nav className="flex flex-wrap gap-x-8 gap-y-2">
              {[
                { label: "会社概要", href: "/about" },
                { label: "事業案内", href: "/service" },
                { label: "お知らせ", href: "/news" },
                { label: "お問い合わせ", href: "/contact" },
                { label: "採用情報", href: "/recruit" },
              ].map((n) => (
                <Link
                  key={n.label}
                  to={n.href}
                  className="text-xs text-gray-600 hover:text-[#3ab5c6] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>

          <p
            className="text-xs text-gray-400 text-center mt-8 font-['Poppins']"
            style={{
              opacity: bottomVisible ? 1 : 0,
              transition: "opacity 0.7s ease 0.3s",
            }}
          >
            © 2026 株式会社FD — FUTURE DIVER. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
